import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { IonicModule } from '@ionic/angular';

import { NavigationService } from '@core/services/navigation/navigation.service';
import { AccountVisibilityService } from '@core/services/products/account-visibility.service';
import { TypeUtilService } from '@core/services/utils/type-util.service';

import { Product, ProductTypes } from '@shared/models/products.model';
import { SharedModule } from '@shared/shared.module';

@Component({
    selector: 'app-spending-limit-inline',
    templateUrl: './spending-limit-inline.component.html',
    styleUrls: ['./spending-limit-inline.component.scss'],
    imports: [CommonModule, IonicModule, FormsModule, SharedModule]
})
export class SpendingLimitInlineComponent implements OnInit, OnChanges {
  @Input() product!: Product;

  @Output() toggleChange = new EventEmitter<boolean>();
  @Output() showRemainingChange = new EventEmitter<boolean>();

  private readonly navigationService = inject(NavigationService);
  private readonly accountVisibilityService = inject(AccountVisibilityService);
  // Пытаемся получить product из инжектора (если передан через NgComponentOutlet)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly injectedProduct = inject<Product | null>('PRODUCT_DATA' as any, { optional: true });
  private initialized = false; // Флаг для предотвращения повторной инициализации

  /** Типы продуктов, для которых доступна установка лимита */
  private static readonly ALLOWED_LIMIT_TYPES: ProductTypes[] = [
    ProductTypes.ACCOUNT,
    ProductTypes.INVESTMENT,
    ProductTypes.SAVE_ACCOUNT,
    ProductTypes.METAL_ACCOUNT
  ];

  // Состояние компонента
  // Включено = режим отображения лимита активен (asBalance=true и заданы лимиты)
  protected isEnabled = false;
  // showRemainingLimit отражает флаг asBalance (показывать остаток лимита вместо остатка средств)
  protected showRemainingLimit = false;
  protected limitAmount = 0; // В копейках для pipe amount
  protected limitRest = 0; // В копейках для pipe amount
  protected isDisabled = false; // Флаг disabled состояния (при forbiddenChange)

  ngOnInit(): void {
    // Если product не передан через @Input, пытаемся получить из инжектора
    if (!this.product && this.injectedProduct) {
      this.product = this.injectedProduct;
    }

    // Лимит доступен только для определённых типов продуктов
    if (!this.product || !SpendingLimitInlineComponent.ALLOWED_LIMIT_TYPES.includes(this.product.productType)) {
      return; // Не инициализируем компонент для неподдерживаемых типов
    }

    // Инициализируем только один раз
    if (!this.initialized && this.product) {
      this.initializeFromProduct();
      this.initialized = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['product']?.currentValue && !this.initialized) {
      this.initializeFromProduct();
      this.initialized = true;
    }
  }

  protected onToggleChange(event: CustomEvent): void {
    const isChecked = event.detail.checked;

    // Если toggle выключается (isChecked === false), снимаем лимит
    if (!isChecked && this.isEnabled) {
      void this.removeLimitFromAccount();

      return;
    }

    // Если toggle включается, открываем страницу настройки лимита
    this.isEnabled = isChecked;
    this.toggleChange.emit(isChecked);

    if (this.product?.id) {
      void this.openLimitSettings();
    }
  }

  protected async onShowRemainingChange(event: CustomEvent): Promise<void> {
    if (this.isDisabled) {
      console.warn('Cannot change showRemaining: forbiddenChange is true for product', this.product?.id);

      return;
    }

    const isChecked = TypeUtilService.isTruthy(event.detail.checked);

    // Сохраняем на сервере (используем controlledLimit)
    const limitForRequest =
      typeof this.product?.controlledLimit === 'number' && this.product.controlledLimit > 0
        ? this.product.controlledLimit
        : undefined;

    if (this.product?.id && typeof limitForRequest === 'number') {
      try {
        await this.accountVisibilityService
          .hideAccount({
            accId: this.product.id,
            hideType: 3,
            hideLimit: limitForRequest,
            asBalance: isChecked,
            isAccount: this.product.productType !== ProductTypes.CARD
          })
          .toPromise();

        // Обновляем локальную модель и вычисляем состояние
        this.product.asBalance = isChecked;
        this.showRemainingLimit = isChecked;
        this.recomputeEnabled();
        this.showRemainingChange.emit(isChecked);
      } catch (error) {
        console.error('Ошибка сохранения asBalance:', error);
      }
    } else {
      this.showRemainingLimit = isChecked;
      this.product.asBalance = isChecked;
      this.recomputeEnabled();
      this.showRemainingChange.emit(isChecked);
    }
  }

  protected async onCheckboxLabelClick(): Promise<void> {
    await this.onShowRemainingChange({ detail: { checked: !this.showRemainingLimit } } as CustomEvent);
  }

  protected async openLimitSettings(): Promise<void> {
    if (!this.product?.id) {
      console.error('Cannot open limit settings: product is undefined or missing id');

      return;
    }

    // Проверка запрета на изменение ограничений (forbiddenChange)
    if (this.isDisabled) {
      console.warn('Cannot open limit settings: forbiddenChange is true for product', this.product.id);

      return;
    }

    // Дополнительная проверка — настройки лимита недоступны для неподдерживаемых типов продуктов
    if (!SpendingLimitInlineComponent.ALLOWED_LIMIT_TYPES.includes(this.product.productType)) {
      console.error('Cannot open limit settings: not available for this product type');
      return;
    }

    // TODO: После обновления на Angular 15+ использовать ngComponentOutletInputs вместо Injector.create()
    // Абсолютный путь к модальному окну установки лимита с передачей product через state
    await this.navigationService.push(`product/${this.product.id}/restrictions/set-spending-limit`, {
      state: { product: this.product }
    });
  }

  /**
   * Проверка, есть ли данные об остатке лимита
   */
  protected get hasLimitRestInfo(): boolean {
    return typeof this.product?.controlledLimitRest === 'number';
  }

  /**
   * Снятие лимита с счета
   */
  private async removeLimitFromAccount(): Promise<void> {
    if (!this.product?.id) {
      console.error('Cannot remove limit: product is undefined or missing id');

      return;
    }

    // Проверяем запрет на изменение ограничений (forbiddenChange)
    // Если установлен, API вернет ошибку 3011: "На продукт с id=XXX наложен запрет по изменению ограничений"
    if (this.isDisabled) {
      console.warn('Cannot remove limit: forbiddenChange is true for product', this.product.id);

      return;
    }

    try {
      // Используем метод removeLimit() для снятия лимита через /rest/personal/v1/acc_visible/remove-limit
      await this.accountVisibilityService.removeLimit(this.product.id).toPromise();

      // Обновляем локальную модель
      this.product.controlledLimit = undefined;
      this.product.controlledLimitRest = undefined;
      this.product.asBalance = false;

      // Сбрасываем состояние компонента
      this.isEnabled = false;
      this.showRemainingLimit = false;
      this.limitAmount = 0;
      this.limitRest = 0;

      this.toggleChange.emit(false);

      console.log('Лимит успешно снят с счета:', this.product.id);
    } catch (error) {
      console.error('Ошибка снятия лимита:', error);

      // Восстанавливаем предыдущее состояние toggle при ошибке
      this.isEnabled = true;
    }
  }

  /**
   * Инициализация состояния из данных продукта
   */
  private initializeFromProduct(): void {
    if (!this.product) {
      return;
    }

    // Приведение типов для лимитов (API может вернуть строки)
    if (typeof this.product.controlledLimit === 'string' && !!this.product.controlledLimit) {
      const parsed = parseFloat(String(this.product.controlledLimit).replace(/\s/g, '').replace(',', '.'));

      this.product.controlledLimit = isNaN(parsed) ? undefined : parsed;
    }
    if (typeof this.product.controlledLimitRest === 'string' && !!this.product.controlledLimitRest) {
      const parsed = parseFloat(String(this.product.controlledLimitRest).replace(/\s/g, '').replace(',', '.'));

      this.product.controlledLimitRest = isNaN(parsed) ? undefined : parsed;
    }

    this.isDisabled = TypeUtilService.isTruthy(this.product.forbiddenChange);

    // Проверяем, установлен ли лимит
    const hasControlledLimit = typeof this.product.controlledLimit === 'number' && this.product.controlledLimit > 0;
    const hasLimitRest = typeof this.product.controlledLimitRest === 'number' && this.product.controlledLimitRest >= 0;

    console.log('SpendingLimit Debug:', {
      controlledLimit: this.product.controlledLimit,
      controlledLimitRest: this.product.controlledLimitRest,
      forbiddenChange: this.product.forbiddenChange,
      isDisabled: this.isDisabled,
      hasControlledLimit,
      hasLimitRest
    });

    // isEnabled должно быть true, если заданы лимиты (независимо от asBalance)
    this.isEnabled = hasControlledLimit && hasLimitRest;

    // showRemainingLimit отражает состояние asBalance
    this.showRemainingLimit = TypeUtilService.isTruthy(this.product.asBalance);

    // Устанавливаем значения лимита
    if (this.product.controlledLimit !== undefined) {
      // Преобразуем в копейки для AmountPipe
      this.limitAmount = Math.round(this.product.controlledLimit);
    }

    // Устанавливаем остаток лимита
    if (this.product.controlledLimitRest !== undefined) {
      this.limitRest = Math.round(this.product.controlledLimitRest);
    }
  }

  private recomputeEnabled(): void {
    const hasControlledLimit = typeof this.product?.controlledLimit === 'number' && this.product.controlledLimit > 0;
    const hasLimitRest = typeof this.product?.controlledLimitRest === 'number' && this.product.controlledLimitRest >= 0;

    // isEnabled зависит только от наличия лимитов, не от asBalance
    this.isEnabled = hasControlledLimit && hasLimitRest;
  }
}
